import React, { Suspense, useEffect } from 'react';
import "bootstrap/dist/css/bootstrap.min.css";
import './App.css';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Header from './Components/Header';
import Footer from './Components/Footer';
// --- UPDATED: Imported useCart to pass user data to CAPI ---
// import FixCache from './FixCache'; // <--- ADD THIS LINE
import { CartProvider, useCart } from './Context/CartContext';
import { ProductProvider } from './Context/ProductContext';
import ProtectedRoute from './Components/ProtectedRoute';
import ReactGA from "react-ga4";
// --- STATIC IMPORTS (Keep critical Home page static for fast LCP) ---
import Home from './Pages/Home/Index'; 
// --- ADDED: Import your new Meta Tracking Helper ---
import { sendServerSideMetaEvent } from './firebase/metaTracking';

// Google Analytics Initialization
// Replace your current ReactGA.initialize line with this:
// Replace your current initialization with this:
ReactGA.initialize("G-VSZJFXMJN5", {
  gaOptions: {
    debug_mode: true,
  },
  gtagOptions: {
    debug_mode: true,
  }
});

// --- LAZY LOAD MODALS (Optimization) ---
// By lazy loading these, we remove their code from the main bundle.
// They will load in the background or when needed, reducing TBT (Total Blocking Time).
const CartModal = React.lazy(() => import('./Components/CartModal/Index'));
const LoginModal = React.lazy(() => import('./Components/LoginModal/Index'));
const SearchModal = React.lazy(() => import('./Components/SearchModal/Index'));
const AddressModal = React.lazy(() => import('./Components/AddressModal/Index'));
const SizeChartModal = React.lazy(() => import('./Components/SizeChartModal'));

// --- LAZY LOAD PAGES (Already good, keeping as is) ---
// Define loader functions so we can both lazy-load and prefetch after idle
const loadShopAll = () => import('./Pages/ShopAll/Index');
const ShopAll = React.lazy(loadShopAll);

const loadProductDetailPage = () => import('./Pages/ProductDetail/Index');
const ProductDetailPage = React.lazy(loadProductDetailPage);

const loadAccessoryDetailPage = () => import('./Pages/ProductDetail/AccessoryDetail');
const AccessoryDetailPage = React.lazy(loadAccessoryDetailPage);

const loadContactPage = () => import('./Pages/ContactPage/Index');
const ContactPage = React.lazy(loadContactPage);

const loadCategoryPage = () => import('./Pages/CategoryPage/Index');
const CategoryPage = React.lazy(loadCategoryPage);

const loadCheckoutPage = () => import('./Pages/CheckoutPage/Index');
const CheckoutPage = React.lazy(loadCheckoutPage);

const loadOrderSuccessPage = () => import('./Pages/OrderSuccess/Index');
const OrderSuccessPage = React.lazy(loadOrderSuccessPage);

const loadMyAccountPage = () => import('./Pages/MyAccountPage/Index');
const MyAccountPage = React.lazy(loadMyAccountPage);

const loadShippingReturnsPage = () => import('./Pages/ShippingPolicy/Index');
const ShippingReturnsPage = React.lazy(loadShippingReturnsPage);

const loadAboutPage = () => import('./Pages/AboutPage/Index');
const AboutPage = React.lazy(loadAboutPage);

const loadOurStoryPage = () => import('./Pages/OurStory/index');
const OurStoryPage = React.lazy(loadOurStoryPage);

const loadBlogPage = () => import('./Pages/BlogPage/index');
const BlogPage = React.lazy(loadBlogPage);

const loadBlogPostPage = () => import('./Pages/BlogPostPage/index');
const BlogPostPage = React.lazy(loadBlogPostPage);

const loadAccessoryPage = () => import('./Pages/AccessoryPage/Index');
const AccessoryPage = React.lazy(loadAccessoryPage);


// --- ADDED: SCROLL TO TOP COMPONENT ---
// This forces the window to scroll to the top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};


// --- GLOBAL PAGE TRACKER COMPONENT ---
// This ensures that every time a user clicks a link, a PageView is sent to both Meta and Google.
const GlobalPageTracker = () => {
  const location = useLocation();
  const { currentUser } = useCart(); // Grab the user state if available

  // --- NEW: Meta Pixel Advanced Matching Initialization ---
  useEffect(() => {
    // Check if the user exists AND if the Meta Pixel (fbq) has loaded
    if (currentUser && window.fbq) {
      
      const advancedMatchingData = {};
      
      // Grab their email
      if (currentUser.email) {
        advancedMatchingData.em = currentUser.email.toLowerCase().trim();
      }
      
      // If you store their phone number in the user object globally, add it too
      if (currentUser.phoneNumber) {
        advancedMatchingData.ph = currentUser.phoneNumber.replace(/\D/g, ''); 
      }

      // Re-initialize the pixel with their data
      window.fbq('init', '1414062377054980', advancedMatchingData);
    }
  }, [currentUser]); // This runs once the moment the user logs in or is detected
  // --------------------------------------------------------

  // --- EXISTING: Page Tracking ---
  useEffect(() => {
    // 1. Meta Browser Pixel
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }

    // 2. Meta Server-Side API (CAPI)
    sendServerSideMetaEvent('PageView', {}, currentUser?.email);
    
    // 3. Google Analytics
    ReactGA.send({ 
      hitType: "pageview", 
      page: location.pathname + location.search,
      title: document.title 
    });
  }, [location, currentUser]); // Re-fires on every URL change or login state change

  return null;
};

// Custom Loader for Pages
const PageLoader = () => (
  <div style={{ padding: '40px', textAlign: 'center' }}>
    Loading...
  </div>
);

// Simple Error Boundary to catch lazy chunk load failures
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          Something went wrong loading this page.
          <div style={{ marginTop: 12 }}>
            <button onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Defer mounting heavy children until the browser is idle to improve LCP
function DeferredMount({ children, delay = 0 }) {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const run = () => setReady(true);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run);
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(run, delay);
    return () => clearTimeout(t);
  }, [delay]);

  return ready ? children : null;
}

function App() {
  // Prefetch common chunks after the browser becomes idle (and if user isn't in data saver)
  React.useEffect(() => {
    const connection = typeof navigator !== 'undefined' && navigator.connection;
    const saveData = !!(connection && connection.saveData);
    const isSlow = !!(connection && /(^|\b)2g\b/.test(connection.effectiveType || ''));
    const shouldPrefetch = !saveData && !isSlow;

    const prefetch = () => {
      if (!shouldPrefetch) return;
      // Prefetch frequently-used modals
      import('./Components/CartModal/Index');
      import('./Components/LoginModal/Index');
      import('./Components/SearchModal/Index');
      import('./Components/AddressModal/Index');
      import('./Components/SizeChartModal');
      // Prefetch likely next routes
      loadProductDetailPage();
      loadAccessoryDetailPage();
      loadCategoryPage();
      loadShopAll();
      loadOurStoryPage();
      loadBlogPage();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch);
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(prefetch, 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <CartProvider>
      <ProductProvider>
        <BrowserRouter future={{ v7_relativeSplatPath: true }}>

        {/* --- ADDED SCROLLTOTOP COMPONENT HERE --- */}
        <ScrollToTop />
        
        <GlobalPageTracker /> 

        {/* <div className="heart-container">
  <span className="heart small">♥</span>
  <span className="heart medium">♥</span>
  <span className="heart large">♥</span>
  <span className="heart small">♥</span>
  <span className="heart medium">♥</span>
  <span className="heart large">♥</span>
  <span className="heart small">♥</span>
  <span className="heart medium">♥</span>
</div> */}


          <Header />
          
          {/* OPTIMIZATION: Wrap Modals in Suspense with fallback={null}.
             This ensures they don't block the UI while loading code chunks.
             fallback={null} means "don't show a spinner, just show nothing until ready".
          */}
          <DeferredMount>
            <Suspense fallback={null}>
              <CartModal /> 
              <LoginModal /> 
              <SearchModal />
              <AddressModal />
              <SizeChartModal />
              {/* <FixCache />  */}
            </Suspense>
          </DeferredMount>
          
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* --- Public Routes --- */}
                <Route path="/" element={<Home />} />
                <Route path="/shop-all" element={<ShopAll />} />
                <Route path="/product/:productId" element={<ProductDetailPage />} />
                <Route path="/accessory/:productId" element={<AccessoryDetailPage />} />
                <Route path="/contact-us" element={<ContactPage />} />
                <Route path="/category/:categoryName" element={<CategoryPage />} />
                <Route path="/shipping-policy" element={<ShippingReturnsPage />} />
                <Route path="/cancellation-refunds" element={<ShippingReturnsPage />} />
                <Route path="/returns-exchanges" element={<ShippingReturnsPage />} />
                <Route path="/about-us" element={<AboutPage />} />
                <Route path="/our-story" element={<OurStoryPage />} />
                <Route path="/accessories" element={<AccessoryPage />} />

                {/* --- NEW BLOG ROUTES --- */}
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:postId" element={<BlogPostPage />} />
                
                {/* --- Protected Routes --- */}
                <Route 
                  path="/checkout" 
                  element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} 
                />
                <Route 
                  path="/order-success/:orderId" 
                  element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} 
                />
                <Route 
                  path="/my-account" 
                  element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} 
                />

                {/* --- Catch-all redirect to home --- */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          
          <Footer />
        </BrowserRouter>
      </ProductProvider>
    </CartProvider>
  );
}

export default App;